import { Either, Some } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description  Сотрудник.
 * @param {string}   birthDate                             Дата рождения.
 * @param {number[]} cabinetIds                            Идентификатор рабочего кабинета.
 * @param {string}   confidentialityLevelId                Идентификатор уровня конфиденциальности.
 * @param {string}   dismissedDate                         Дата увольнения.
 * @param {string}   email                                 Адрес электронной почты.
 * @param {string}   emailStatus                           Статус электронной почты.
 * @param {string}   roleId                                Идентификатор роли
 * @param {boolean}  hasEditableAccess                     Имеет изменяемый доступ.
 * @param {boolean}  hasTelegramByPhone                    Имеет ли Telegram по номеру.
 * @param {boolean}  hasWhatsAppByPhone                    Имеет ли WhatsApp по номеру.
 * @param {number}   id                                    Идентификатор работника.
 * @param {string}   info                                  Дополнительная информация.
 * @param {string}   inn                                   ИНН.
 * @param {boolean}  isAllowedCashbox                      Разрешены операции с кассой.
 * @param {boolean}  isAllowedLogin                        Разрешен ли доступ к Панели.
 * @param {boolean}  isBranch                              Филиальный.
 * @param {boolean}  isDismissed                           Уволен.
 * @param isEmailVerified
 * @param {boolean}  isEnabledWorkingScheduling            Включен ли в рабочее расписание.
 * @param {boolean}  isForbidSelectOtherCabinetsInSchedule Запретить ставить сотрудника в другие
 * @param {boolean}  isOwner                               Это владелец
 * @param {string}   name                                  Имя.
 * @param {array}    nsiPositions                          Должности по ЕГИСЗ НСИ.
 * @param {string}   patronymic                            Отчество.
 * @param {string}   phone                                 Номер телефона.
 * @param {string}   iin                                   ИИН.
 * @param {string}   snils                                 СНИЛС.
 * @param {array}    specialities                          Специальности.
 * @param {array}    specialityName                        Наименование специальности.
 * @param {string}   surname                               Фамилия.
 * @param {number}   timeStepMinutes                       Шаг расписания в минутах.
 * @param {boolean}  hasUser
 * @param {string}   notifyChannel                         Канал уведомления.
 * @param {string}   lpuId                                 Идентификатор ЛПУ.
 * @param {Object}   errors                                Ошибки.
 * @param {boolean}  hasBranchWorkPlaces                   Имеет филиальные рабочие места.
 * @constructor
 */
function Employee(
  birthDate,
  cabinetIds,
  confidentialityLevelId,
  dismissedDate,
  email,
  emailStatus,
  roleId,
  hasEditableAccess,
  hasTelegramByPhone,
  hasWhatsAppByPhone,
  id,
  info,
  inn,
  isAllowedCashbox,
  isAllowedLogin,
  isBranch,
  isDismissed,
  isEmailVerified,
  isEnabledWorkingScheduling,
  isForbidSelectOtherCabinetsInSchedule,
  isOwner,
  name,
  nsiPositions,
  patronymic,
  phone,
  iin,
  snils,
  specialities,
  specialityName,
  surname,
  timeStepMinutes,
  hasUser,
  notifyChannel,
  lpuId,
  errors = {},
  hasBranchWorkPlaces = false,
) {
  this.birthDate = birthDate;
  this.cabinetIds = cabinetIds;
  this.confidentialityLevelId = confidentialityLevelId;
  this.dismissedDate = dismissedDate;
  this.email = email;
  this.emailStatus = emailStatus;
  this.roleId = roleId;
  this.hasEditableAccess = hasEditableAccess;
  this.hasTelegramByPhone = hasTelegramByPhone;
  this.hasWhatsAppByPhone = hasWhatsAppByPhone;
  this.id = id;
  this.info = info;
  this.inn = inn;
  this.isAllowedCashbox = isAllowedCashbox;
  this.isAllowedLogin = isAllowedLogin;
  this.isBranch = isBranch;
  this.isDismissed = isDismissed;
  this.isEmailVerified = isEmailVerified;
  this.isEnabledWorkingScheduling = isEnabledWorkingScheduling;
  this.isForbidSelectOtherCabinetsInSchedule = isForbidSelectOtherCabinetsInSchedule;
  this.isOwner = isOwner;
  this.name = name;
  this.nsiPositions = nsiPositions;
  this.patronymic = patronymic;
  this.phone = phone;
  this.iin = iin;
  this.snils = snils;
  this.specialities = specialities;
  this.specialityName = specialityName;
  this.surname = surname;
  this.timeStepMinutes = timeStepMinutes;
  this.hasUser = hasUser;
  this.notifyChannel = notifyChannel;
  this.lpuId = lpuId;
  this.errors = errors;
  this.hasBranchWorkPlaces = hasBranchWorkPlaces;
}

/**
 * @description Запрос создания сотрудника.
 * @param {string[]} additionalPermissions                 Дополнительные разрешения.
 * @param {string}   birthDate                             Дата рождения.
 * @param {string[]} cabinetIds                            Идентификаторы кабинетов.
 * @param {string}   confidentialityLevelId                Идентификатор конфиденциальности.
 * @param {string}   dismissedDate                         Дата увольнения.
 * @param {string}   email                                 Адрес электронной почты.
 * @param {string[]} excludedPermissions                   Исключенные разрешения.
 * @param {string}   roleId                                Идентификаторы должностей.
 * @param {boolean}  hasTelegramByPhone                    Имеет ли Telegram по номеру.
 * @param {boolean}  hasWhatsAppByPhone                    Имеет ли WhatsApp по номеру.
 * @param {string}   inn                                   ИНН.
 * @param {string}   info                                  Дополнительная информация.
 * @param {boolean}  isAllowedCashbox                      Разрешены операции с кассой.
 * @param {boolean}  isAllowedLogin                        Разрешен ли доступ к Панели.
 * @param {boolean}  isDismissed                           Уволен.
 * @param {boolean}  isEnabledWorkingScheduling            Включен ли в рабочее расписание.
 * @param {boolean}  isForbidSelectOtherCabinetsInSchedule Запретить ставить сотрудника в другие
 *                                                         кабинеты в расписании
 * @param {string}   name                                  Имя.
 * @param {boolean}  needsEgisz                            Заполнить данные ЕГИСЗ.
 * @param {string}   nsiPositionId                         Должность по ЕГИСЗ НСИ.
 * @param {boolean}  needsSendCredentialsMessage           Нужно ли отправить сообщение с учетными
 *                                                         данными.
 * @param {string}   patronymic                            Отчество.
 * @param {string}   phone                                 Номер телефона.
 * @param {string}   iin                                   ИИН.
 * @param {string}   snils                                 СНИЛС.
 * @param {number}   specialtyId                           Идентификатор специальности.
 * @param {string}   specialtyName                         Специализация сотрудника
 * @param {string}   surname                               Фамилия.
 * @param {number}   timeStepMinutes                       Шаг расписания в минутах.
 * @constructor
 */
export function EmployeeCreationRequest(
  additionalPermissions,
  birthDate,
  cabinetIds,
  confidentialityLevelId,
  dismissedDate,
  email,
  excludedPermissions,
  roleId,
  hasTelegramByPhone,
  hasWhatsAppByPhone,
  info,
  inn,
  isAllowedCashbox,
  isAllowedLogin,
  isDismissed,
  isEnabledWorkingScheduling,
  isForbidSelectOtherCabinetsInSchedule,
  name,
  needsEgisz,
  nsiPositionId,
  needsSendCredentialsMessage,
  patronymic,
  phone,
  iin,
  snils,
  specialtyId,
  specialtyName,
  surname,
  timeStepMinutes,
) {
  /**
   * @description Преобразовать в формат запроса.
   * @returns {any}
   */
  this.toQuery = () => ({
    additional_permissions: additionalPermissions,
    birth_date: birthDate,
    cabinet_ids: cabinetIds,
    confidentiality_level_id: confidentialityLevelId,
    dismissed_date: dismissedDate,
    email,
    excluded_permissions: excludedPermissions,
    role_id: roleId,
    has_telegram_by_phone: hasTelegramByPhone,
    has_whatsapp_by_phone: hasWhatsAppByPhone,
    info,
    inn,
    is_allowed_cashbox: isAllowedCashbox,
    is_allowed_login: isAllowedLogin,
    is_dismissed: isDismissed,
    is_enabled_working_scheduling: isEnabledWorkingScheduling,
    is_forbid_select_other_cabinets_in_schedule: isForbidSelectOtherCabinetsInSchedule,
    name,
    needs_egisz: needsEgisz,
    needs_send_credentials_message: needsSendCredentialsMessage,
    nsi_position_id: nsiPositionId,
    patronymic,
    phone,
    iin,
    snils,
    specialty_id: specialtyId,
    specialty_name: specialtyName,
    surname,
    time_step_minutes: timeStepMinutes,
  });
}

/**
 * @description Запрос редактирования сотрудника.
 * @param {string}   birthDate                             Дата рождения.
 * @param {string[]} cabinetIds                            Идентификаторы кабинетов.
 * @param {string}   confidentialityLevelId                Идентификатор конфиденциальности.
 * @param {string}   dismissedDate                         Дата увольнения.
 * @param {string}   email                                 Адрес электронной почты.
 * @param {string}   roleId                                Идентификатор роли.
 * @param {boolean}  hasTelegramByPhone                    Имеет ли Telegram по номеру.
 * @param {boolean}  hasWhatsAppByPhone                    Имеет ли WhatsApp по номеру.
 * @param {number}   id                                    Идентификатор сотрудника.
 * @param {string}   info                                  Дополнительная информация.
 * @param {string}   inn                                   ИНН.
 * @param {boolean}  isAllowedCashbox                      Разрешены операции с кассой.
 * @param {boolean}  isAllowedLogin                        Разрешен ли доступ к Панели.
 * @param {boolean}  isDismissed                           Уволен.
 * @param {boolean}  isEnabledWorkingScheduling            Включен ли в рабочее расписание.
 * @param {boolean}  isForbidSelectOtherCabinetsInSchedule Запретить ставить сотрудника в другие
 *                                                         кабинеты в расписании
 * @param {string}   name                                  Имя.
 * @param {boolean}  needsEgisz                            Нужны данные для ЕГИСЗ.
 * @param {string[]} nsiPositionIds                        Должности по ЕГИСЗ НСИ.
 * @param {string}   patronymic                            Отчество.
 * @param {string}   phone                                 Номер телефона.
 * @param {string}   iin                                   ИИН.
 * @param {string}   snils                                 СНИЛС.
 * @param {number[]} specialtyIds                          Идентификаторы специальности.
 * @param {string}   specialtyName                         Специализация сотрудника
 * @param {string}   surname                               Фамилия.
 * @param {number}   timeStepMinutes                       Шаг расписания в минутах.
 * @param {EditEmployeePermissionListRequest} permissionsRequest Запрос на изменение прав
 * @constructor
 */
export function EmployeeEditRequest(
  birthDate,
  cabinetIds,
  confidentialityLevelId,
  dismissedDate,
  email,
  roleId,
  hasTelegramByPhone,
  hasWhatsAppByPhone,
  id,
  info,
  inn,
  isAllowedCashbox,
  isAllowedLogin,
  isDismissed,
  isEnabledWorkingScheduling,
  isForbidSelectOtherCabinetsInSchedule,
  name,
  needsEgisz,
  nsiPositionIds,
  patronymic,
  phone,
  iin,
  snils,
  specialtyIds,
  specialtyName,
  surname,
  timeStepMinutes,
  permissionsRequest,
) {
  /**
   * @description Преобразовать в формат запроса.
   * @returns {any}
   */
  this.toQuery = () => ({
    birth_date: birthDate,
    cabinet_ids: cabinetIds,
    confidentiality_level_id: confidentialityLevelId,
    dismissed_date: dismissedDate,
    email,
    role_id: roleId,
    has_telegram_by_phone: hasTelegramByPhone,
    has_whatsapp_by_phone: hasWhatsAppByPhone,
    info,
    inn,
    is_allowed_cashbox: isAllowedCashbox,
    is_allowed_login: isAllowedLogin,
    is_dismissed: isDismissed,
    is_enabled_working_scheduling: isEnabledWorkingScheduling,
    is_forbid_select_other_cabinets_in_schedule: isForbidSelectOtherCabinetsInSchedule,
    name,
    needs_egisz: needsEgisz,
    nsi_position_ids: nsiPositionIds,
    patronymic,
    phone,
    iin,
    snils,
    specialty_ids: specialtyIds,
    specialty_name: specialtyName,
    surname,
    time_step_minutes: timeStepMinutes,
  });

  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => id;
  /**
   * @description Получить запрос на редактирование разрешений
   * @returns {EditEmployeePermissionListRequest}
   */
  this.getPermissions = () => permissionsRequest;
}

/**
 * @description Запрос редактирования основных параметров сотрудника.
 * @param {number}   id                                    Идентификатор сотрудника.
 * @param {string}   birthDate                             Дата рождения.
 * @param {string}   dismissedDate                         Дата увольнения.
 * @param {string}   email                                 Адрес электронной почты.
 * @param {string}   roleId                                Идентификатор роли.
 * @param {boolean}  hasTelegramByPhone                    Имеет ли Telegram по номеру.
 * @param {boolean}  hasWhatsAppByPhone                    Имеет ли WhatsApp по номеру.
 * @param {boolean}  isAllowedCashbox                      Разрешены операции с кассой.
 * @param {boolean}  isAllowedLogin                        Разрешен ли доступ к Панели.
 * @param {boolean}  isEnabledWorkingScheduling            Включен в расписание.
 * @param {boolean}  isDismissed                           Уволен.
 * @param {string}   name                                  Имя.
 * @param {string}   patronymic                            Отчество.
 * @param {string}   phone                                 Номер телефона.
 * @param {string}   specialtyName                         Специализация сотрудника
 * @param {string}   surname                               Фамилия.
 * @param {string}   iin                                   ИИН.
 * @param {string}   snils                                 СНИЛС.
 * @param {EditEmployeePermissionListRequest} permissionsRequest Запрос на изменение прав
 * @constructor
 */
export function EmployeeBasicEditRequest(
  id,
  birthDate,
  dismissedDate,
  email,
  hasTelegramByPhone,
  hasWhatsAppByPhone,
  isAllowedCashbox,
  isAllowedLogin,
  isDismissed,
  isEnabledWorkingScheduling,
  name,
  patronymic,
  phone,
  roleId,
  specialtyName,
  surname,
  iin,
  snils,
  permissionsRequest,
) {
  /**
   * @description Преобразовать в формат запроса.
   * @returns {any}
   */
  this.toQuery = () => ({
    birth_date: birthDate,
    dismissed_date: dismissedDate,
    email,
    role_id: roleId,
    has_telegram_by_phone: hasTelegramByPhone,
    has_whatsapp_by_phone: hasWhatsAppByPhone,
    is_allowed_cashbox: isAllowedCashbox,
    is_allowed_login: isAllowedLogin,
    is_dismissed: isDismissed,
    is_enabled_working_scheduling: isEnabledWorkingScheduling,
    name,
    patronymic,
    phone,
    specialty_name: specialtyName,
    surname,
    iin,
    snils,
  });

  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => id;

  /**
   * @description Получить запрос на редактирование разрешений
   * @returns {EditEmployeePermissionListRequest}
   */
  this.getPermissions = () => permissionsRequest;
}

/**
 * @description Запрос редактирования сотрудника.
 * @param {number}   employeeId                            Идентификатор сотрудника.
 * @param {string[]} cabinetIds                            Идентификаторы кабинетов.
 * @param {string}   info                                  Дополнительная информация.
 * @param {boolean}  isEnabledWorkingScheduling            Включен ли в рабочее расписание.
 * @param {boolean}  isForbidSelectOtherCabinetsInSchedule Запретить ставить сотрудника в другие
 *                                                         кабинеты в расписании
 * @param {number}   timeStepMinutes                       Шаг расписания в минутах.
 * @param {string}   notifyChannel                         Канал уведомления.
 * @param {string}   dispatchRouting                       Мессенджер отправки.
 * @constructor
 */
export function EmployeeAdmissionEditRequest(
  employeeId,
  cabinetIds,
  info,
  isEnabledWorkingScheduling,
  isForbidSelectOtherCabinetsInSchedule,
  timeStepMinutes,
  notifyChannel,
  dispatchRouting,
) {
  /**
   * @description Преобразовать в формат запроса.
   * @returns {any}
   */
  this.toQuery = () => ({
    cabinet_ids: cabinetIds,
    info,
    is_enabled_working_scheduling: isEnabledWorkingScheduling,
    is_forbid_select_other_cabinets_in_schedule: isForbidSelectOtherCabinetsInSchedule,
    time_step_minutes: timeStepMinutes,
    notify_channel: notifyChannel,
    dispatch_routing: dispatchRouting,
  });

  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => employeeId;
}

/**
 * @description Запрос редактирования ЕГИСЗ сотрудника.
 * @param {number}   employeeId              Идентификатор сотрудника.
 * @param {string}   confidentialityLevelId  Идентификатор конфиденциальности.
 * @param {string}   nsiPositionsIds         Должности по ЕГИСЗ НСИ.
 * @param {array}    specialtiesIds          Идентификаторы специальности.
 * @param {string}   lpuId                   Идентификатор ЛПУ.
 * @constructor
 */
export function EmployeeEgiszEditRequest(
  employeeId,
  confidentialityLevelId,
  nsiPositionsIds,
  specialtiesIds,
  lpuId,
) {
  /**
   * @description Преобразовать в формат запроса.
   * @returns {any}
   */
  this.toQuery = () => ({
    confidentiality_level_id: confidentialityLevelId,
    nsi_positions_ids: nsiPositionsIds,
    specialties_ids: specialtiesIds,
    lpu_id: lpuId,
  });

  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => employeeId;
}

/**
 * @description Запрос на изменение списка разрешений сотрудника.
 * @param {number} employeeId   Идентификатор сотрудника.
 * @param {string[]} additional Идентификаторы добавленных разрешений.
 * @param {string[]} excluded   Идентификаторы исключенных разрешений.
 * @constructor
 */
export function EditEmployeePermissionListRequest(employeeId, additional, excluded) {
  this.getEmployeeId = () => employeeId;
  this.toQuery = () => ({
    additional,
    excluded,
  });
}

/**
 * @description Шлюз сотрудников.
 * @param {AxiosStatic} axios Http клиент.
 * @constructor
 */
export default function EmployeeGateway(axios) {
  /**
   * @description Добавление нового рабочего места.
   * @param {number} employeeId
   * @param {number} branchClinicId
   * @returns {Promise<Either>}
   */
  this.addNewWorkPlace = async (employeeId, branchClinicId) => axios
    .post(`/api/employees/${employeeId}/work-places/${branchClinicId}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить список сотрудников для показа.
   * @param {any} viewQuery Запрос.
   * @returns {Promise<any[]>}
   */
  this.getListForView = async (viewQuery) => {
    const prepareQuery = () => {
      const query = {};
      if (viewQuery) {
        if (viewQuery.match) {
          query.match = viewQuery.match;
        }
        if (viewQuery.filteringByAccessAllowed === true) {
          query.access = 1;
        }
        if (viewQuery.filteringByEnableScheduling === true) {
          query.scheduling = 1;
        }
        if (viewQuery.filteringByDismissed === true) {
          query.dismissed = 1;
        }
      }
      return query;
    };
    const requestUrl = '/api/employees';
    try {
      const response = await axios.get(requestUrl, { params: prepareQuery() });
      const employees = (response.data.data.items || []).map(
        (item) => ({
          cabinet: {
            name: item.cabinet_name,
            color: item.cabinet_color,
          },
          cdaCompleteness: item.cda_completeness || 'unknown',
          fullName: item.full_name,
          id: item.id,
          specialityName: item.speciality_name,
          roleName: item.role_name,
          dismissedDate: item.dismissed_date,
        }),
      );
      const onBoardingStatus = response.data.data.on_boarding_status;
      return Either.Right({ actions: response.data.actions, employees, onBoardingStatus });
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response) {
          return Either.Left(error.response.data.title);
        }
        throw new Error(`Network error: сервер по адресу ${requestUrl} не ответил на запрос`);
      }
      return Either.Left('Произошла непредвиденная ошибка');
    }
  };

  /**
   * @description Изменить пароль.
   * @param {number} employeeId
   * @returns {Promise<Either>}
   */
  this.changePassword = (employeeId) => axios
    .post(`/api/employees/${employeeId}/change-password`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить нового сотрудника.
   * @param {EmployeeCreationRequest} request Запрос создания.
   * @returns {Promise<Either>}
   */
  this.create = (request) => axios
    .post('/api/employees', request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить основное место работы сотрудника.
   * @param {number} employeeId
   * @param {number} branchId
   * @returns {Promise<Either>}
   */
  this.changeMainWorkPlace = (employeeId, branchId) => axios
    .post(`/api/employees/${employeeId}/work-places/${branchId}/main`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Уволить сотрудника в месте работы.
   * @param {number} employeeId
   * @param {string} dismissedDate
   * @param {number} selectedBranchId
   * @returns {Promise<Either>}
   */
  this.dismissOnWorkPlace = (employeeId, dismissedDate, selectedBranchId) => axios
    .delete(
      `/api/employees/${employeeId}/work-places/${selectedBranchId}/dismiss`,
      { params: { dismissed_date: dismissedDate } },
    ).then(
      async (response) => Either.right(response.data.title),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить сотрудника.
   * @param {EmployeeEditRequest} request Запрос редактирования.
   * @returns {Promise<Either>}
   */
  this.edit = async (request) => axios
    .put(`/api/employees/${request.getEmployeeId()}`, request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Изменить основные данные сотрудника.
   * @param {EmployeeBasicEditRequest} request Запрос редактирования.
   * @returns {Promise<Either>}
   */
  this.editBasic = async (request) => axios
    .put(`/api/employees/${request.getEmployeeId()}/basic`, request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Изменить данные приема сотрудника.
   * @param {EmployeeAdmissionEditRequest} request Запрос редактирования.
   * @returns {Promise<Either>}
   */
  this.editAdmission = async (request) => axios
    .put(`/api/employees/${request.getEmployeeId()}/admission`, request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Изменить данные ЕГИСЗ сотрудника.
   * @param {EmployeeEgiszEditRequest} request Запрос редактирования.
   * @returns {Promise<Either>}
   */
  this.editEgisz = async (request) => axios
    .put(`/api/employees/${request.getEmployeeId()}/egisz`, request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить рабочие места сотрудника.
   * @param {number} employeeId
   * @returns {Promise<Either>}
   */
  this.fetchWorkPlaces = async (employeeId) => axios
    .get(`/api/employees/${employeeId}/work-places`)
    .then(
      async (response) => Either.right({
        workPlaces: response.data.work_places,
        actions: response.data.actions,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить заполнители рабочего места сотрудника.
   * @param {number} employeeId
   * @returns {Promise<Either>}
   */
  this.fetchWorkPlaceFillers = async (employeeId) => axios
    .get(`/api/employees/${employeeId}/work-place-fillers`)
    .then(
      async (response) => Either.right({
        branchedClinics: response.data.branched_clinics,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить данные сотрудника по идентификатору.
   * @param {number} id Идентификатор сотрудника.
   * @returns {Promise<Either>}
   */
  this.getById = async (id) => {
    const formatIssuedDate = (date) => {
      const [y, m, d] = date.split('-');
      return [d, m, y].join('/');
    };

    try {
      const response = await axios.get(`/api/employees/${id}`);
      const employee = new Employee(
        response.data.birth_date ? formatIssuedDate(response.data.birth_date) : '',
        response.data.cabinet_ids,
        response.data.confidentiality_level_id,
        response.data.dismissed_date,
        response.data.email,
        response.data.email_status,
        response.data.role_id,
        response.data.has_editable_access,
        response.data.has_telegram_by_phone,
        response.data.has_whatsapp_by_phone,
        response.data.id,
        response.data.info,
        response.data.inn,
        response.data.is_allowed_cashbox,
        response.data.is_allowed_login,
        response.data.is_branch,
        response.data.is_dismissed,
        response.data.is_email_verified,
        response.data.is_enabled_working_scheduling,
        response.data.is_forbid_select_other_cabinets_in_schedule,
        response.data.is_owner,
        response.data.name,
        response.data.nsi_positions,
        response.data.patronymic,
        response.data.phone,
        response.data.iin,
        response.data.snils,
        response.data.specialties,
        response.data.specialty_name,
        response.data.surname,
        response.data.time_step_minutes,
        response.data.has_user,
        response.data.notify_channel,
        response.data.lpu_id,
        response.data.errors,
        response.data.has_branch_work_places,
      );
      return Either.Right(employee);
    } catch (error) {
      if (error.isAxiosError) {
        return Either.Left(error.response.data);
      }
      return Either.Left(
        {
          causes: [],
          error,
          title: 'Произошла непредвиденная ошибка.',
        },
      );
    }
  };
  this.getEmployeeName = async (id) => {
    try {
      const response = await axios.get(`/api/employees/${id}/name`);
      const employeeName = {
        dismissedDate: response.data.dismissed_date,
        id,
        isDismissed: response.data.is_dismissed,
        isAdmin: response.data.is_admin,
        name: response.data.name,
        role: response.data.role,
        hasBasicErrors: response.data.has_basic_errors,
        hasEGISZErrors: response.data.has_egisz_errors,
      };
      return Either.Right(employeeName);
    } catch (error) {
      if (error.isAxiosError) {
        return Either.Left(error.response.data);
      }
      return Either.Left(
        {
          causes: [],
          error,
          title: 'Произошла непредвиденная ошибка.',
        },
      );
    }
  };

  /**
   * @description Получить настройки приема сотрудника.
   * @param {number} id Идентификатор сотрудника.
   * @returns {Promise<Either>}
   */
  this.viewAdmission = (id) => axios
    .get(`/api/employees/${id}/admission`)
    .then(
      (response) => Either.right({
        id: response.data.id,
        availableNotificationChannels: response.data.available_notification_channels,
        cabinetIds: response.data.cabinet_ids,
        info: response.data.info,
        isDismissed: response.data.is_dismissed,
        isEnabledWorkingScheduling: response.data.is_enabled_working_scheduling,
        isForbidSelectOtherCabinetsInSchedule:
          response.data.is_forbid_select_other_cabinets_in_schedule,
        notifyChannel: response.data.notify_channel,
        dispatchRouting: response.data.dispatch_routing,
        timeStepMinutes: response.data.time_step_minutes,
      }),
      (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить рабочие дни сотрудника.
   * @param {number}  employeeId   Идентификатор сотрудника.
   * @param {string}  yearAndMonth Год и месяц.
   * @param {boolean} isNow        От текущей даты.
   * @returns {Promise<Either>}
   */
  this.viewWorkDays = async (employeeId, yearAndMonth, isNow = true) => {
    const params = { year_and_month: yearAndMonth, now: isNow ? 1 : 0 };
    return axios
      .get(`/api/employees/${employeeId}/monthly-work-schedule`, { params })
      .then(
        async (response) => Either.right(response.data),
        async (error) => {
          if (error.isAxiosError && error.response.status === 400) {
            return Either.left(error.response.data);
          }
          throw error;
        },
      );
  };
  /**
   * Получить список докторов.
   */
  this.getDoctorList = async () => {
    const getSurnameWithInitials = (fullName) => {
      let result = '';
      const [surname, name, patronymic] = fullName.split(' ');
      if (surname) {
        result += surname;
      }
      if (name) {
        result += ` ${name.substring(0, 1)}.`;
      }
      if (patronymic) {
        result += ` ${patronymic.substring(0, 1)}.`;
      }
      return result;
    };
    const response = await axios.get('/api/doctors');
    return Some(
      (response.data.data || [])
        .map((item) => ({
          id: item.id,
          name: getSurnameWithInitials(item.full_name),
          position: item.position_names,
        })),
    );
  };

  /**
   * @description Получить список сотрудников исполнителей.
   * @returns {Promise<Maybe>}
   */
  this.viewEmployeePerformerList = () => axios
    .get('/api/employee-performers')
    .then(
      (response) => {
        const { employees } = response.data.data;
        const employeePerformer = response.data.data.employee_performer;
        return Either.right({ employees, employeePerformer });
      },
    );

  /**
   * @description Получить список разрешений по идентификатору сотрудника.
   * @param {number} employeeId Идентификатор сотрудника.
   * @returns {Promise<Either>}
   */
  this.viewPermissionListByEmployeeId = (employeeId) => axios
    .get(`/api/employees/${employeeId}/permissions`)
    .then(
      async (result) => {
        const permissions = result.data.data.map((group) => ({
          name: group.name,
          permissions: group.permissions.map((permission) => ({
            id: permission.id,
            name: permission.name,
            value: permission.value,
            isContains: permission.is_contains,
          })),
          withoutGroup: group.without_group,
        }));
        return Either.right(permissions);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование списка разрешений сотрудника.
   * @param {EditEmployeePermissionListRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.editPermissionListByEmployeeId = (request) => axios
    .put(`/api/employees/${request.getEmployeeId()}/permissions`, request.toQuery())
    .then(
      async (result) => Either.right(result.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );
}
